import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CrossCircleIconXs } from '@lichtblick/icons/svg/cross-circle/cross-circle-xs';
import { SearchIconXs } from '@lichtblick/icons/svg/search/search-xs';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import { doesValueMatchSearch } from '../ComboBoxInput/ComboBoxInput.helpers';
import { FieldFooter } from '../FieldFooter/FieldFooter';
import { Label } from '../Label/Label';
import { IconWrapper, Input, InputRow, LabelWrapper, OptionContent, OptionLabel, Slot, Wrapper } from './FilterMultiSelect.styles';
/**
 * Displays the `placeholder` when not focussed. Use this to display a meaningful summary of the selected values.
 */ export const FilterMultiSelect = /*#__PURE__*/ forwardRef(({ className, 'data-testid': testId, icon, id, isDisabled, label, name, onChange, onFocus, options, statusLabel, statusType, values, ...props }, ref)=>{
    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const inputId = id || name;
    const labelId = inputId && `${inputId}_label`;
    const statusId = inputId && `${inputId}_validationMessage`;
    const filteredOptions = useMemo(()=>options.filter(({ keywords })=>doesValueMatchSearch(keywords.join(), searchValue)).map(({ label, value })=>({
                label: /*#__PURE__*/ _jsxs(OptionContent, {
                    children: [
                        /*#__PURE__*/ _jsx(Checkbox, {
                            checked: values?.includes(value),
                            tabIndex: -1
                        }),
                        /*#__PURE__*/ _jsx(OptionLabel, {
                            children: label
                        })
                    ]
                }),
                value
            })), [
        options,
        searchValue,
        values
    ]);
    const setValue = ({ target })=>setSearchValue(target.value);
    const clearValue = (event)=>{
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setSearchValue('');
        }
    };
    const openDropdown = (event)=>{
        dropdownRef.current?.open();
        onFocus?.(event);
    };
    const focusInput = ()=>inputRef.current?.focus();
    const dispatchChange = (value)=>{
        const valuesSet = new Set(values);
        valuesSet.delete(value) || valuesSet.add(value);
        onChange?.({
            target: {
                value: Array.from(valuesSet),
                name
            }
        });
    };
    const clearInput = ()=>{
        setSearchValue('');
        focusInput();
    };
    return /*#__PURE__*/ _jsxs(Wrapper, {
        className: className,
        children: [
            /*#__PURE__*/ _jsx(LabelWrapper, {
                children: Boolean(label) && /*#__PURE__*/ _jsx(Label, {
                    htmlFor: inputId,
                    id: labelId,
                    isBold: true,
                    children: label
                })
            }),
            /*#__PURE__*/ _jsxs(InputRow, {
                $isDisabled: isDisabled,
                $statusType: statusType,
                onBlur: clearValue,
                onClose: focusInput,
                onOptionSelect: dispatchChange,
                options: filteredOptions,
                ref: dropdownRef,
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        name: name,
                        type: "hidden",
                        value: values
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        ...props,
                        "aria-describedby": statusId,
                        "aria-invalid": statusType === 'error',
                        "aria-labelledby": labelId,
                        "data-testid": testId || inputId,
                        disabled: isDisabled,
                        id: inputId,
                        onChange: setValue,
                        onFocus: openDropdown,
                        ref: (instance)=>{
                            if (typeof ref === 'function') {
                                ref(instance);
                            } else if (ref) {
                                ref.current = instance;
                            }
                            inputRef.current = instance;
                        },
                        value: searchValue
                    }),
                    !isDisabled && /*#__PURE__*/ _jsxs(Slot, {
                        children: [
                            Boolean(searchValue) && /*#__PURE__*/ _jsx(IconWrapper, {
                                onClick: clearInput,
                                children: /*#__PURE__*/ _jsx(CrossCircleIconXs, {})
                            }),
                            /*#__PURE__*/ _jsx(IconWrapper, {
                                onClick: focusInput,
                                children: /*#__PURE__*/ _jsx(SearchIconXs, {})
                            })
                        ]
                    })
                ]
            }),
            Boolean(statusLabel) && /*#__PURE__*/ _jsx(FieldFooter, {
                fieldFooterLabel: statusLabel,
                fieldFooterType: statusType,
                icon: icon,
                id: statusId
            })
        ]
    });
});

//# sourceMappingURL=FilterMultiSelect.js.map